if (!customElements.get('card-product-slider')) {
  class CardProductSlider extends HTMLElement {
    constructor() {
      super();

      const swiperOptions =
        JSON.parse(this.getAttribute('data-swiper-options')) || {};

      this.swiperOptions = swiperOptions;

      this.sliderWrapper = this.querySelector('.swiper-wrapper');
      this.requiresFetch = this.hasAttribute('data-url');
      this.parent = this.closest('[data-section-id]');

      /*
       * Number of cards the section is configured to display.
       * Used primarily by Recommended Products so Shopify
       * recommendations can be topped up with fallback products.
       */
      this.productsLimit =
        Number(this.getAttribute('data-products-limit')) || 0;

      /*
       * Store the initial fallback slides before the recommendation
       * request replaces/changes anything.
       */
      this.fallbackSlides = [];

      if (this.requiresFetch && this.sliderWrapper) {
        this.fallbackSlides = Array.from(
          this.sliderWrapper.querySelectorAll(
            '[data-fallback-slide="true"]'
          )
        ).map(slide => slide.cloneNode(true));
      }

      /*
       * Use desktop slides-per-view supplied by Liquid.
       * Older sections that don't supply one continue to use 4.
       */
      const slidesPerViewDesktop =
        Number(swiperOptions.slidesPerViewDesktop) || 4;

      this.sliderOptions = {
        a11y: false,
        threshold: 3,
        spaceBetween: swiperOptions.spaceBetweenMobile || 2,
        slidesPerView: swiperOptions.slidesPerViewMobile || 'auto',
        resistanceRatio: 0.72,

        breakpoints: {
          750: {
            slidesPerView: 3,
            spaceBetween: swiperOptions.spaceBetweenDesktop || 2
          },

          1100: {
            slidesPerView: slidesPerViewDesktop,
            spaceBetween: swiperOptions.spaceBetweenDesktop || 2
          }
        }
      };

      if (swiperOptions.navigation) {
        this.sliderOptions.navigation = {
          ...swiperOptions.navigation
        };
      }

      if (swiperOptions.autoplay) {
        this.sliderOptions.autoplay = {
          ...swiperOptions.autoplay
        };
      }

      if (swiperOptions.loop) {
        this.sliderOptions.loop = swiperOptions.loop;
      }
    }

    connectedCallback() {
      if (this.requiresFetch) {
        this.fetchItems();
        return;
      }

      this.initSlider();
      this.syncHeights();

      window.addEventListener(
        'resize',
        () => this.syncHeights()
      );
    }

    syncHeights() {
      const textSlide = this.querySelector(
        '.products-feed__card-text-custom-slide'
      );

      const actionElement = this.querySelector(
        '.product-card__actions'
      );

      if (textSlide && actionElement) {
        const actionHeight = actionElement.offsetHeight;

        textSlide.style.height = `${actionHeight}px`;
      }
    }

    fetchItems() {
      if (!this.parent) {
        throw new Error('Parent is missing');
      }

      fetch(this.getAttribute('data-url'))
        .then(response => response.text())
        .then(text => {
          const html = new DOMParser().parseFromString(
            text,
            'text/html'
          );

          const fetchedWrapper = html.querySelector(
            '.swiper-wrapper'
          );

          if (!fetchedWrapper) {
            return;
          }

          const fetchedSlides = Array.from(
            fetchedWrapper.querySelectorAll(
              '.swiper-slide'
            )
          );

          /*
           * Build final slides:
           *
           * 1. Shopify recommendations first.
           * 2. Original fallback products afterwards.
           * 3. Skip duplicate product IDs.
           * 4. Stop at the section's selected productsLimit.
           */
          const finalSlides = [];
          const usedProductIds = new Set();

          const addSlide = slide => {
            if (!slide) return;

            if (
              this.productsLimit > 0 &&
              finalSlides.length >= this.productsLimit
            ) {
              return;
            }

            const productId =
              slide.getAttribute('data-product-id');

            if (
              productId &&
              usedProductIds.has(productId)
            ) {
              return;
            }

            if (productId) {
              usedProductIds.add(productId);
            }

            finalSlides.push(
              slide.cloneNode(true)
            );
          };

          /*
           * Recommendations always get priority.
           */
          fetchedSlides.forEach(slide => {
            addSlide(slide);
          });

          /*
           * Fill any remaining positions using the fallback
           * products rendered on the original product page.
           */
          this.fallbackSlides.forEach(slide => {
            addSlide(slide);
          });

          /*
           * Replace the carousel with the merged result.
           */
          this.sliderWrapper.innerHTML = '';

          finalSlides.forEach(slide => {
            slide.removeAttribute(
              'data-fallback-slide'
            );

            this.sliderWrapper.appendChild(slide);
          });

          /*
           * Only reveal the section if we actually have products.
           */
          if (finalSlides.length > 0) {
            this.parent.classList.remove('hidden');

            this.initSlider();
            this.syncHeights();
          }
        })
        .catch(e => {
          console.error(e);
        });
    }

    initSlider(slider = this) {
      /*
       * If already initialized, destroy the existing Swiper
       * before rebuilding it.
       */
      if (
        this.slider &&
        typeof this.slider.destroy === 'function'
      ) {
        this.slider.destroy(true, true);
      }

      /*
       * Re-read the current options before initialization so
       * Shopify theme editor changes are respected.
       */
      const swiperOptions =
        JSON.parse(
          this.getAttribute('data-swiper-options')
        ) || {};

      this.swiperOptions = swiperOptions;

      const slidesPerViewDesktop =
        Number(swiperOptions.slidesPerViewDesktop) || 4;

      this.sliderOptions.spaceBetween =
        swiperOptions.spaceBetweenMobile || 2;

      this.sliderOptions.slidesPerView =
        swiperOptions.slidesPerViewMobile || 'auto';

      /*
       * Tablet stays at the theme's existing 3.
       * Desktop uses the section's selected 4 or 5.
       */
      this.sliderOptions.breakpoints = {
        750: {
          slidesPerView: 3,
          spaceBetween:
            swiperOptions.spaceBetweenDesktop || 2
        },

        1100: {
          slidesPerView: slidesPerViewDesktop,
          spaceBetween:
            swiperOptions.spaceBetweenDesktop || 2
        }
      };

      /*
       * Refresh navigation from the current settings.
       */
      if (swiperOptions.navigation) {
        this.sliderOptions.navigation = {
          ...swiperOptions.navigation
        };
      } else {
        delete this.sliderOptions.navigation;
      }

      /*
       * Refresh autoplay from the current settings.
       */
      if (swiperOptions.autoplay) {
        this.sliderOptions.autoplay = {
          ...swiperOptions.autoplay
        };
      } else {
        delete this.sliderOptions.autoplay;
      }

      /*
       * Refresh loop if supplied.
       */
      if (swiperOptions.loop) {
        this.sliderOptions.loop =
          swiperOptions.loop;
      } else {
        delete this.sliderOptions.loop;
      }

      this.slider = new Swiper(
        slider,
        this.sliderOptions
      );

      this.slider.wrapperEl.setAttribute(
        'aria-live',
        'polite'
      );

      this.slider.wrapperEl.id =
        `swiper-wrapper-${this.dataset.sectionId}`;

      this.slider.slides.forEach(
        (slide, i) => {
          slide.setAttribute(
            'role',
            'group'
          );

          slide.setAttribute(
            'aria-label',
            `${i + 1} / ${this.slider.slides.length}`
          );

          slide.dataset.index = i;
        }
      );

      if (
        swiperOptions.navigation &&
        this.slider.navigation
      ) {
        const sliderButtons = [
          this.slider.navigation.prevEl,
          this.slider.navigation.nextEl
        ];

        sliderButtons.forEach(
          (button, i) => {
            if (!button) return;

            let type = 'Previous';

            if (i === 1) {
              type = 'Next';
            }

            button.setAttribute(
              'aria-label',
              `${type} slide`
            );

            button.setAttribute(
              'aria-controls',
              this.slider.wrapperEl.id
            );
          }
        );
      }

      this.slider.wrapperEl.addEventListener(
        'focusin',
        e => {
          this.slider.el.scrollLeft = 0;

          const slide = e.target.closest(
            '.swiper-slide'
          );

          if (!slide) return;

          const slideIndex =
            +slide.dataset.index;

          const isFirstSlide =
            slideIndex === 0;

          const isLastSlide =
            slideIndex ===
            this.slider.slides.length - 1;

          if (
            isFirstSlide ||
            isLastSlide
          ) {
            this.slider.slideTo(
              slideIndex,
              0
            );

            return;
          }

          const slideRect =
            slide.getBoundingClientRect();

          const wrapperWidth =
            this.slider.wrapperEl.clientWidth;

          // prettier-ignore
          const slideLeft = slideRect.x + slideRect.width - ((document.body.clientWidth - wrapperWidth) / 2);

          const isSlideVisible =
            slideLeft <= wrapperWidth &&
            slideLeft >= slideRect.width;

          if (isSlideVisible) {
            return;
          }

          slideLeft >= slideRect.width
            ? this.slider.slideNext(0)
            : this.slider.slidePrev(0);
        }
      );

      /*
       * Stop autoplay while hovering.
       */
      this.slider.wrapperEl.addEventListener(
        'mouseenter',
        () => {
          if (
            this.slider.autoplay &&
            typeof this.slider.autoplay.stop ===
              'function'
          ) {
            this.slider.autoplay.stop();
          }
        }
      );

      /*
       * Resume autoplay after hover, unless Quick Cart
       * is currently open.
       */
      this.slider.wrapperEl.addEventListener(
        'mouseleave',
        () => {
          if (
            !this.classList.contains(
              'product--open-on-quick-cart'
            ) &&
            this.sliderOptions.autoplay &&
            this.slider.autoplay &&
            typeof this.slider.autoplay.start ===
              'function'
          ) {
            this.slider.autoplay.start();
          }

          if (
            swiperOptions.autoplay === false &&
            this.slider.autoplay &&
            typeof this.slider.autoplay.stop ===
              'function'
          ) {
            this.slider.autoplay.stop();
          }
        }
      );

      /*
       * Quick Cart initialization.
       */
      if (
        this.hasAttribute(
          'data-init-quick-cart'
        ) &&
        document.querySelector(
          'quick-cart-drawer'
        ) &&
        this.querySelector(
          '.swiper-slide'
        )
      ) {
        document
          .querySelector(
            'quick-cart-drawer'
          )
          .init();
      }

      /*
       * Re-initialize the individual product-card
       * custom elements after async content has loaded.
       */
      this.querySelectorAll(
        'product-card'
      ).forEach(card => {
        if (
          typeof card.init ===
          'function'
        ) {
          card.init();
        }
      });

      if (Shopify.designMode) {
        if (
          this.hasAttribute(
            'data-init-quick-cart'
          ) &&
          document.querySelector(
            'quick-cart-drawer'
          ) &&
          this.querySelector(
            '.swiper-slide'
          )
        ) {
          document
            .querySelector(
              'quick-cart-drawer'
            )
            .init();
        }
      }
    }
  }

  customElements.define(
    'card-product-slider',
    CardProductSlider
  );
}